import { useState, useEffect, useCallback } from 'react';
import { getAdminStatus, type AdminStatus } from '~/services/joc-club-admins';
import { useAccount } from '~/hooks/with-account';

/**
 * Resolve the connected wallet's admin status from the server (`/api/admin/me`),
 * which authorizes against both owners (env) and KV-stored admins.
 */
export function useAdminStatus() {
  const { account } = useAccount();
  const connectedAddress = account?.ethAddress?.toLowerCase() || '';
  const [status, setStatus] = useState<AdminStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    if (!connectedAddress) {
      setStatus(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const result = await getAdminStatus();
      setStatus(result);
    } catch {
      setStatus({ ethAddress: connectedAddress, isAdmin: false, isOwner: false });
    } finally {
      setLoading(false);
    }
  }, [connectedAddress]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return {
    connectedAddress,
    isAdmin: status?.isAdmin ?? false,
    isOwner: status?.isOwner ?? false,
    loading,
    refetch: fetchStatus,
  };
}
